import React, { useState, useEffect, useRef, useCallback } from "react";
import "./SpecialSideBar.css";
import { FiChevronDown } from "react-icons/fi";

function SpecialSideBar({ Cluster, sidebartitle }) {
  const [openCluster, setOpenCluster] = useState(null);
  const [activeDimension, setActiveDimension] = useState(null);
  const [topOffset, setTopOffset] = useState(140);
  const [sidebarHeight, setSidebarHeight] = useState(600);
  const [isMobile, setIsMobile] = useState(false);
  
  const sidebarRef = useRef(null);
  const scrollingRef = useRef(false);
  const observerRef = useRef(null);

  const toggleCluster = (index) => {
    setOpenCluster(openCluster === index ? null : index);
  };

  const handleScroll = useCallback((dimension, clusterIndex) => {
    scrollingRef.current = true;
    const targetId = `dimension-${dimension.id}`;
    const target = document.getElementById(targetId);

    if (target) {
      const topPos = target.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({
        top: topPos,
        behavior: "smooth",
      });

      setActiveDimension(dimension.id);
      setOpenCluster(clusterIndex);

      setTimeout(() => {
        scrollingRef.current = false;
      }, 1000);
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      const dashboard = document.querySelector(".dashboard-header");
      const dashboardHeight = dashboard?.getBoundingClientRect?.()?.height || 0;
      const scrollY = window.scrollY || 0;
      const viewportHeight = window.innerHeight || 800;
      const docHeight = document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;
      const distanceFromBottom = Math.max(0, docHeight - (scrollY + viewportHeight));

      let offset = dashboardHeight + 20;
      if (distanceFromBottom < 100) {
        offset = Math.max(0, offset - (100 - distanceFromBottom));
      }
      setTopOffset(Math.max(offset, 140));

      const availableHeight = viewportHeight - offset - 40;
      setSidebarHeight(Math.min(Math.max(availableHeight, 300), 750));
    };

    updateDimensions();
    
    const throttledUpdate = () => {
      requestAnimationFrame(updateDimensions);
    };

    window.addEventListener("scroll", throttledUpdate, { passive: true });
    window.addEventListener("resize", throttledUpdate, { passive: true });

    document.body.style.setProperty("overflow-x", "visible", "important");
    document.documentElement.style.setProperty("overflow-x", "visible", "important");

    return () => {
      window.removeEventListener("scroll", throttledUpdate);
      window.removeEventListener("resize", throttledUpdate);
    };
  }, []);

  useEffect(() => {
    if (!Cluster || Cluster.length === 0) return;

    const allDimensionIds = Cluster.flatMap((cluster, clusterIndex) =>
      cluster?.dimension?.map((dim) => ({
        id: dim.id,
        clusterIndex,
      })) || []
    );

    if (allDimensionIds.length === 0) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -60% 0px", 
      threshold: [0, 0.25, 0.5, 0.75, 1],
    };

    const observerCallback = (entries) => {
      if (scrollingRef.current) return;

      let maxVisibility = 0;
      let mostVisibleEntry = null;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxVisibility) {
          maxVisibility = entry.intersectionRatio;
          mostVisibleEntry = entry;
        }
      });

      if (mostVisibleEntry) {
        const dimensionId = parseInt(
          mostVisibleEntry.target.id.replace("dimension-", "")
        );
        const dimensionData = allDimensionIds.find((d) => d.id === dimensionId);

        if (dimensionData) {
          setActiveDimension(dimensionId);
          setOpenCluster(dimensionData.clusterIndex);
        }
      }
    };

    observerRef.current = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe all dimension elements
    allDimensionIds.forEach(({ id }) => {
      const element = document.getElementById(`dimension-${id}`);
      if (element) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [Cluster]);

  useEffect(() => {
    if (!sidebarRef.current || activeDimension === null) return;

    const activeItem = sidebarRef.current.querySelector(
      `[data-dimension-id="${activeDimension}"]`
    );

    if (activeItem) {
      const container = sidebarRef.current.querySelector(".sidebar-scroll-container");
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const itemRect = activeItem.getBoundingClientRect();

        const isVisible =
          itemRect.top >= containerRect.top &&
          itemRect.bottom <= containerRect.bottom;

        if (!isVisible) {
          const scrollTop =
            activeItem.offsetTop -
            container.offsetTop -
            container.clientHeight / 2 +
            activeItem.clientHeight / 2;

          container.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: "smooth",
          });
        }
      }
    }
  }, [activeDimension]);

  if (!Cluster || Cluster.length === 0) {
    return null;
  }

  return (
    <div
      ref={sidebarRef}
      className="sidebar-menu"
      style={{
        position: "sticky",
        top: `${topOffset}px`,
        // maxHeight: isMobile ? "auto" : `${sidebarHeight}px`,
        overflow: "hidden",
        zIndex: 100,
        willChange: "transform",
      }}
    >
      {sidebartitle && <h4>{sidebartitle}</h4>}

      <div
        className="sidebar-scroll-container"
        style={{
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {Cluster.map((cluster, clusterIndex) => (
          <div key={clusterIndex} className="cluster">
            <div
              className={`cluster-header ${
                openCluster === clusterIndex ? "active" : ""
              }`}
              onClick={() => toggleCluster(clusterIndex)}
            >
              <span>{cluster.name}</span>
              <FiChevronDown
                className={`chevron ${
                  openCluster === clusterIndex ? "rotate" : ""
                }`}
              />
            </div>

            {openCluster === clusterIndex &&
              cluster?.dimension &&
              cluster.dimension.length > 0 && (
                <ul className="cluster-items">
                  {cluster.dimension.map((item, idx) => (
                    <li
                      key={idx}
                      data-dimension-id={item.id}
                      className={`cluster-item ${
                        activeDimension === item.id ? "active" : ""
                      }`}
                      onClick={() => handleScroll(item, clusterIndex)}
                    >
                      {item.name}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default SpecialSideBar;